import type { Entity, Link, LinkType } from '@/types/entity';
import { DIRECTED_LINK_TYPES } from '@/types/entity';
import type { Project } from '@/types/project';
import { CANON } from './canon';
import { MANUSCRIPT_SCENES } from './manuscript';
import { SEED_TS, SCHEMA_VERSION, PROJECT_ID } from '@/lib/constants';

/**
 * 바이블 v3(CANON) → 엔티티/링크/Project 변환 (M1).
 * 결정적(deterministic): 안정 id + 고정 타임스탬프 → export/import roundtrip 무손실.
 * location·faction·theme·관계는 CANON 산문에서 합성(PRD §7).
 */

export interface SeedBundle {
  project: Project;
  entities: Entity[];
  links: Link[];
}

// CSS 변수 참조 → hex (canon.ts의 ac/lane 색). globals.css의 --l-*/--p-* 토큰과 동기 유지.
const VAR_HEX: Record<string, string> = {
  'var(--l-main)': '#46c2ec',
  'var(--l-sisters)': '#f06d8a',
  'var(--l-tar)': '#ec8a4a',
  'var(--l-forgium)': '#8c84ee',
  'var(--l-aoe)': '#ecd45c',
  'var(--p-pyro)': '#bd6ee8',
};
const hex = (v: string) => VAR_HEX[v] ?? v;

// 유효 플롯 컬럼 id (CANON.plot.cols 단일 진실). 원고 비트의 col이 보드 컬럼과 어긋나면 고아가 되므로 정규화한다.
const VALID_PLOT_COLS = new Set(CANON.plot.cols.map((c) => c.id));

// 행성 5색 — globals.css --p-* 토큰과 동기(색온도 분리: 청록/초록/금/주황/보라).
const PLANET_COLOR: Record<string, string> = {
  forgium: '#22d3cb',
  arcadia: '#5cd667',
  elysion: '#f0cf5a',
  tar: '#ec7d4a',
  pyro: '#bd6ee8',
};
// 철학 축 좌표 (분석 0 ↔ 합일 1)
const PLANET_AXIS: Record<string, number> = {
  forgium: 0.12,
  arcadia: 0.9,
  elysion: 0.25,
  tar: 0.5,
  pyro: 0.72,
};

// '—' 같은 빈 표식 정리
const clean = (s?: string) => (s && s.trim() !== '—' ? s : '');
const firstClause = (s: string) => s.split(/[—.\n(]/)[0]!.trim().slice(0, 64);

function entityBase(): Pick<
  Entity,
  'canonStatus' | 'confidence' | 'version' | 'createdAt' | 'updatedAt' | 'sourceRefs'
> {
  return {
    canonStatus: 'canon',
    confidence: 'confirmed',
    version: 1,
    createdAt: SEED_TS,
    updatedAt: SEED_TS,
    sourceRefs: [{ kind: 'bible', ref: 'v3' }],
  };
}

function linkOf(from: string, to: string, type: LinkType, label?: string): Link {
  return {
    id: `link_${from}_${to}_${type}`,
    from,
    to,
    type,
    ...(label ? { label } : {}),
    directed: DIRECTED_LINK_TYPES.includes(type),
    canonStatus: 'canon',
    confidence: 'confirmed',
    version: 1,
  };
}

/** 행성 하위 장소(8) — CANON 산문에서 합성 (PRD §7) */
const LOCATION_SEED: { id: string; name: string; planet: string; kind: string; desc: string }[] = [
  { id: 'loc-sigma', name: '시그마', planet: 'forgium', kind: '수도', desc: '매끈하고 완벽히 통제된 차가운 핵심부, 권력의 자리.' },
  { id: 'loc-neoncity', name: '네온시티', planet: 'forgium', kind: '위성도시', desc: '최첨단 문화 복합 도시, 엔지니어들의 천국. 겉은 차갑고 속은 긱.' },
  { id: 'loc-oasisprima', name: '오아시스 프리마', planet: 'tar', kind: '도시(舊 에덴)', desc: '타르의 오아시스 도시. 프리마의 일상.' },
  { id: 'loc-sconce', name: '스콘스', planet: 'tar', kind: '갱단 거점', desc: '빅터 스콘의 갱단 본거지. 보이던스를 운영.' },
  { id: 'loc-voidance', name: '보이던스', planet: 'tar', kind: '요새 감옥', desc: '나올 수 없는 요새 감옥. 실체는 셀 실험장.' },
  { id: 'loc-jaina', name: '자이나사원', planet: 'pyro', kind: '사원', desc: '파이로크라시아 수행의 중심. 예언자 아이샤 알마.' },
  { id: 'loc-phantom', name: '팬텀시티', planet: 'pyro', kind: '지하도시', desc: '지하세계 — 밀수와 환락, 몬테카를로.' },
  { id: 'loc-akenion', name: '아케니온', planet: 'elysion', kind: '아고라', desc: '엘리시온의 아고라. 베일에 싸인 권력의 자리.' },
];

/** 세력(4) — CANON 산문에서 합성 (PRD §7) */
const FACTION_SEED: { id: string; name: string; desc: string; alignment: string; goal: string }[] = [
  { id: 'fac-aoe', name: 'A.O.E', desc: '범우주기업 — 초거성을 캐 에테르를 얻고 아이온을 추진한다.', alignment: '분석 · 통제', goal: '전지의 인공지성 아이온 완성.' },
  { id: 'fac-elysion', name: '엘리시온', desc: '얼굴 없는 진짜 권력. 모든 시나리오의 설계자.', alignment: '순혈 · 승천', goal: '초인류 / 새로운 이브.' },
  { id: 'fac-sconce', name: '스콘스', desc: '타르의 갱단. 요새 감옥 보이던스를 운영.', alignment: '무법 · 힘', goal: '타르 지배.' },
  { id: 'fac-forgium-council', name: '포지움 위원회', desc: '통제 vs 자유의 토론장 — 루이 · 렌 츠바시 · 이안.', alignment: '공학 · 합리', goal: '포지움 운영 방향 결정.' },
];

const STORYLINE_LEAD: Record<string, string> = {
  main: 'radia',
  sisters: 'rasia',
  tar: 'kylis',
  forgium: 'ian',
  aoe: 'numan',
};

/** 관계 시드 (PRD §7) */
const RELATION_SEED: [string, string, LinkType][] = [
  ['radia', 'rasia', 'sibling'],
  ['rasia', 'kylis', 'lover'],
  ['ian', 'continental', 'creator'],
  ['ian', 'son', 'mentor'],
  ['victor', 'kylis', 'rival'],
  ['numan', 'kylis', 'rival'],
  ['radia', 'alma', 'mirror'],
  ['elysion', 'continental', 'mirror'],
  ['ian', 'ren', 'mirror'], // 통제축 척추 — 자유(주입)의 이안 vs 통제의 렌
  ['rasia', 'kylis', 'mirror'], // '위험한 거울' — 라시아가 닮아선 안 될 미래(연인이자 foil)
];

const MEMBERSHIP_SEED: [string, string][] = [
  ['rasia', 'fac-sconce'],
  ['kylis', 'fac-sconce'],
  ['victor', 'fac-sconce'],
  ['numan', 'fac-aoe'],
  ['ian', 'fac-forgium-council'],
  ['ren', 'fac-forgium-council'],
  ['louis', 'fac-forgium-council'],
];

const ENGINE_TIME_SCOPES = {
  setup: {
    facts: ['rasia_joins_sconce', 'aoe_machine_started'],
  },
  rising: {
    facts: ['earth_etherized_truth_revealed', 'victor_challenged', 'continental_awakened', 'radia_offer_made', 'preorder_code_approved'],
  },
  climax: {
    facts: ['radia_aiyon_fusion', 'preorder_code_traps_radia', 'victor_deposed', 'kylis_rules_sconce', 'numan_falls_to_tar', 'radia_backup_seeded', 'aiyon_destroyed'],
  },
  p2: {
    facts: ['aoe_patronage_withdrawn', 'voidance_collapse', 'tar_power_vacuum'],
    lawless: true,
  },
  p3: {
    facts: ['radia_reborn_as_new_eve'],
  },
};

const ENGINE_CAUSALITY = [
  { id: 'cause-kylis-rules-sconce', effect: 'kylis_rules_sconce', requires: ['victor_deposed'] },
  { id: 'cause-aiyon-prison', effect: 'preorder_code_traps_radia', requires: ['preorder_code_approved', 'radia_aiyon_fusion'] },
  { id: 'cause-voidance-collapse', effect: 'voidance_collapse', requires: ['aoe_patronage_withdrawn'] },
  { id: 'cause-tar-power-vacuum', effect: 'tar_power_vacuum', requires: ['voidance_collapse'] },
  { id: 'cause-radia-rebirth', effect: 'radia_reborn_as_new_eve', requires: ['radia_backup_seeded'] },
];

const ENGINE_CONSTRAINTS = [
  {
    id: 'C5-character',
    type: 'character',
    rule: '인물의 wound·lie·desire·need·arc와 장면 행동이 충돌하면 후보로 제안한다.',
    check: 'llm_proposed',
  },
  {
    id: 'C7-tone',
    type: 'tone',
    rule: '정답 선언이 아니라 선택을 남기는 Starkillers 톤을 보존한다.',
    check: 'llm_proposed',
  },
];

const FACTION_ENGINE_META: Record<string, Record<string, unknown>> = {
  'fac-aoe': {
    lawful: true,
    controls: ['forgium', 'loc-sigma'],
    economics: '에테르 채굴과 아이온 프로젝트를 통한 범우주 통제',
  },
  'fac-elysion': {
    lawful: false,
    controls: ['elysion', 'loc-akenion'],
    economics: '초인류 설계와 시나리오 통제',
  },
  'fac-sconce': {
    lawful: false,
    controls: ['loc-sconce', 'loc-voidance'],
    economics: '보이던스 운영과 A.O.E 후원에 기대는 타르 권력',
    sustained_by: 'res-aoe-patronage',
  },
  'fac-forgium-council': {
    lawful: true,
    controls: ['forgium', 'loc-sigma'],
    economics: '포지움 운영 방향과 공학 인프라 통제',
  },
};

const ENGINE_RESOURCE_SEED = [
  {
    id: 'res-aoe-patronage',
    name: 'A.O.E 후원 흐름',
    definition: 'A.O.E가 타르 권력 구조와 보이던스 운영에 제공하는 후원·거래·방치의 흐름.',
    engine: {
      kind: 'resource_flow',
      flow: 'A.O.E 후원',
      fragility: '아이온 완성·A.O.E 철수 이후 타르 권력 토대가 무너진다.',
      collapses_in: ['p2'],
    },
  },
];

export function buildSeed(): SeedBundle {
  const entities: Entity[] = [];
  const links: Link[] = [];

  // ── theme (2): 개요 레벨 명제 (인간vs기계·보편적죄는 concept로 별도 존재) ──
  entities.push({
    id: 'theme-stance',
    type: 'theme',
    name: '작품 입장',
    summary: CANON.meta.stance,
    fields: { statement: CANON.meta.stance, axis: '', basis: CANON.meta.stanceSub },
    ...entityBase(),
  });
  entities.push({
    id: 'theme-axis',
    type: 'theme',
    name: '철학 축 — 분석 ↔ 합일',
    summary: CANON.meta.axisKey,
    fields: {
      statement: CANON.meta.axisKey,
      axis: '분석 ↔ 합일',
      basis: `${CANON.meta.axisA.k}: ${CANON.meta.axisA.v}\n${CANON.meta.axisB.k}: ${CANON.meta.axisB.v}`,
    },
    ...entityBase(),
  });

  // ── planet (5) ──
  for (const p of CANON.planets) {
    entities.push({
      id: p.id,
      type: 'planet',
      name: p.name,
      aliases: [p.rom],
      summary: p.tag,
      color: PLANET_COLOR[p.id],
      fields: {
        tag: p.tag,
        env: p.env,
        culture: p.culture,
        admin: p.admin,
        philosophy: p.philosophy,
        citizens: p.citizens,
        cities: p.cities,
        role: p.role,
        axis: PLANET_AXIS[p.id] ?? 0.5,
      },
      ...entityBase(),
    });
  }

  // ── location (8) + located_in links ──
  for (const l of LOCATION_SEED) {
    entities.push({
      id: l.id,
      type: 'location',
      name: l.name,
      summary: l.kind,
      fields: { parent: l.planet, kind: l.kind, desc: l.desc },
      ...entityBase(),
    });
    links.push(linkOf(l.id, l.planet, 'located_in'));
  }

  // ── character (12) ──
  for (const c of CANON.characters) {
    entities.push({
      id: c.id,
      type: 'character',
      name: c.name,
      summary: c.role,
      color: hex(c.ac),
      fields: {
        role: c.role,
        tier: c.tier,
        line: clean(c.line),
        wound: clean(c.wound),
        lie: clean(c.lie),
        desire: clean(c.desire),
        need: clean(c.need),
        flaw: clean(c.flaw),
        arc: clean(c.arc),
      },
      ...entityBase(),
    });
  }

  // ── faction (4) + member_of links ──
  for (const f of FACTION_SEED) {
    entities.push({
      id: f.id,
      type: 'faction',
      name: f.name,
      summary: f.alignment,
      fields: { desc: f.desc, alignment: f.alignment, goal: f.goal },
      ...(FACTION_ENGINE_META[f.id] ? { meta: { engine: FACTION_ENGINE_META[f.id] } } : {}),
      ...entityBase(),
    });
  }
  for (const [who, fac] of MEMBERSHIP_SEED) links.push(linkOf(who, fac, 'member_of'));

  // ── concept (12) ──
  CANON.concepts.forEach((c, i) => {
    entities.push({
      id: `concept-${i + 1}`,
      type: 'concept',
      name: c.t,
      summary: firstClause(c.d),
      fields: { definition: c.d, themeFunction: '' },
      ...entityBase(),
    });
  });
  for (const resource of ENGINE_RESOURCE_SEED) {
    entities.push({
      id: resource.id,
      type: 'concept',
      name: resource.name,
      summary: firstClause(resource.definition),
      fields: { definition: resource.definition, themeFunction: '거시 자원 흐름' },
      meta: { engine: resource.engine },
      ...entityBase(),
    });
  }

  // ── storyline (5) ──
  for (const lane of CANON.plot.lanes) {
    entities.push({
      id: `sl-${lane.id}`,
      type: 'storyline',
      name: lane.t,
      color: hex(lane.c),
      fields: { label: lane.t, color: hex(lane.c), lead: STORYLINE_LEAD[lane.id] ?? '' },
      ...entityBase(),
    });
  }

  // ── beat (24) — 셀(storyline:col)별 order 부여 ──
  const orderCounter: Record<string, number> = {};
  for (const b of CANON.plot.beats) {
    const storylineId = `sl-${b.lane}`;
    const cellKey = `${storylineId}:${b.col}`;
    const order = orderCounter[cellKey] ?? 0;
    orderCounter[cellKey] = order + 1;
    entities.push({
      id: b.id,
      type: 'beat',
      name: b.title,
      summary: b.desc,
      color: hex(`var(--l-${b.lane})`),
      fields: {
        storylineId,
        col: b.col,
        order,
        summary: b.desc,
        pov: '',
        goal: '',
        conflict: '',
        characters: [],
        location: '',
      },
      ...entityBase(),
    });
  }

  // ── 원고 비트(WS0-2) — 작성된 씬을 beat로(bodyDraft=본문, draft 상태). orderCounter 이어붙여 계획 비트 아래 적층 ──
  for (const s of MANUSCRIPT_SCENES) {
    const storylineId = `sl-${s.storyline}`;
    // 원고 col을 유효 플롯 컬럼으로 정규화(미지정/구문값 'Part 1' 등 → 전개). location은 loc-* 부재 시 planet ref 허용.
    const col = VALID_PLOT_COLS.has(s.col) ? s.col : 'rising';
    const cellKey = `${storylineId}:${col}`;
    const order = orderCounter[cellKey] ?? 0;
    orderCounter[cellKey] = order + 1;
    entities.push({
      id: s.id,
      type: 'beat',
      name: s.name,
      summary: s.summary,
      color: hex(`var(--l-${s.storyline})`),
      fields: {
        storylineId,
        col,
        order,
        summary: s.summary,
        pov: s.pov,
        goal: '',
        conflict: '',
        characters: s.characters,
        location: s.location,
        bodyDraft: s.body,
      },
      ...entityBase(),
      canonStatus: 'draft',
      sourceRefs: [{ kind: 'manuscript', ref: '원고모음' }],
    });
  }

  // ── Part 1 인과 척추(WS1) — beat→beat 인과 링크(directed). 문서 plot-frame-part1.md의 하중 척추와 동기화. ──
  // 모든 엣지는 setup<rising<climax 순방향(DAG, 순환 없음). r1·r3 무유입·c4 단일유입·누만 chain 단절을 메운다.
  const CAUSAL_P1: [string, string][] = [
    // 발단 → 전개
    ['s1', 's2'], ['s1', 's3'], ['s2', 's4'], ['s2', 'r1'],
    // 전개 내부 · 압력 누적
    ['s3', 'r2'], ['s4', 'r3'], ['s4', 'r5'], ['s4', 'r6'], ['r4', 'r6'],
    // 전개 → 절정
    ['r1', 'c1'], ['r4', 'c1'], ['r5', 'c1'], ['r6', 'c2'], ['r6', 'c4'], ['r4', 'c4'], ['r4', 'c3'], ['r2', 'c3'], ['r3', 'c5'], ['r5', 'c6'],
    // 절정 내부 연쇄
    ['c1', 'c2'], ['c2', 'c3'], ['c1', 'c7'], ['c6', 'c7'], ['c3', 'c6'], ['c3', 'c8'], ['c4', 'c8'], ['c5', 'c8'], ['c6', 'c8'],
  ];
  for (const [from, to] of CAUSAL_P1) {
    links.push({ id: `link_${from}_${to}_cause`, from, to, type: 'related', label: '인과', directed: true, canonStatus: 'canon', confidence: 'confirmed', version: 1 });
  }

  // ── question (8) — canonStatus='question' (PRD §4.5) ──
  CANON.open.forEach((q, i) => {
    entities.push({
      id: `question-${i + 1}`,
      type: 'question',
      name: q.t,
      summary: firstClause(q.d),
      fields: { desc: q.d, status: 'open', resolution: '' },
      ...entityBase(),
      canonStatus: 'question',
    });
  });

  // ── 관계 링크 ──
  for (const [from, to, type] of RELATION_SEED) links.push(linkOf(from, to, type));

  // ── project ──
  const project: Project = {
    id: PROJECT_ID,
    title: CANON.meta.title,
    schemaVersion: SCHEMA_VERSION,
    plotColumns: CANON.plot.cols.map((c, i) => ({
      id: c.id,
      label: c.t,
      phase: c.ph,
      order: i,
    })),
    meta: { ...CANON.meta, engine: { timeScopes: ENGINE_TIME_SCOPES, causality: ENGINE_CAUSALITY, constraints: ENGINE_CONSTRAINTS } },
    createdAt: SEED_TS,
    updatedAt: SEED_TS,
  };

  return { project, entities, links };
}

/** 테스트/검증용 기대 개수 */
export const SEED_EXPECTED = {
  byType: {
    theme: 2,
    planet: 5,
    location: 8,
    character: 14,
    faction: 4,
    concept: 15,
    storyline: 5,
    beat: 37,
    question: 8,
    note: 0,
  } as Record<string, number>,
  entities: 98, // v3.2: concept 12→14 + engine resource flow 1
  links: 53, // located_in 8 + member_of 7 + CAUSAL_P1 28 + RELATION_SEED 10
};
